/* The on-screen movement stick, as a state machine.
 *
 * This lived inline in `main.js` as a set of listeners writing to two module
 * globals, and the consequence was a class of bug that kept coming back: a
 * finger that is physically still on the glass, and a game that thinks it
 * lifted. Three separate fixes were written for three instances of it — a
 * screen fade, closing the map, the end of a launch — and the third one did
 * not work, because the launch's own fix cleared the record the recovery read
 * from twelve lines later. Measured: with UP held through a launch,
 * `input.up` was false for 0 of 38 samples afterwards, and a 1px nudge fixed
 * it instantly.
 *
 * The distinction the old code did not make, and this one is built on:
 *
 *   - `held` is a record of pointer EVENTS. It is what the last event said.
 *   - `pointers` is a record of FINGERS. A finger that never lifted is still
 *     down, and its last known position is still an instruction.
 *
 * So steering is always DERIVED from the fingers, never remembered. A launch,
 * a fade or a map does not clear anything; it makes `steering` false, and the
 * moment it goes true again the same fingers produce the same direction with
 * nothing to restore.
 *
 * IT IS A STICK, NOT FOUR BUTTONS WEARING A CIRCLE.
 *
 * This file used to answer "which of four directions" and then, after
 * diagonals were asked for, "which of eight sectors" — with a hysteresis rule
 * to stop a thumb near a boundary flipping per event, and a second rule to
 * stop the first rule sticking. A thumb points wherever it points. Quantising
 * that to 45 degrees means seven of every eight headings a player can express
 * are not the heading they get, and every one of those rules existed only to
 * paper over the quantisation.
 *
 * So the stick reports a VECTOR and nothing else. There are no sectors, so
 * there is no boundary, so there is nothing to be sticky about — the whole
 * hysteresis apparatus is gone rather than fixed. (An examiner found the
 * sticky rule was being fed the raw sector instead of the reported one and so
 * never engaged at all: 19 flips in 19 move events at 22.5 degrees. Deleting
 * the machinery deletes that bug and the four more it could still have had.)
 *
 * The keyboard still speaks in four flags, because a keyboard has four keys.
 * `Player.update` takes either and prefers the vector.
 */

/** Inside this fraction of the pad's WIDTH from centre, nothing is commanded.
 *  A thumb resting in the middle is a hand on the phone, not an input.
 *
 *  A FRACTION OF THE WIDTH IS TWICE THE FRACTION OF THE RADIUS. At 0.16 this
 *  read as "16% dead" and measured as 32% of the throw: on a 75.4px radius
 *  the first live input was 25px out — 4.14mm, a third of the way to the rim
 *  — and with full deflection reached at 78%, every analog value a player can
 *  express lived in the band between 32% and 78%. Under half the travel doing
 *  all the work. 0.075 of the width is 15% of the radius, about 2mm: still
 *  more than a resting thumb wanders, and it gives the range back. */
export const DEADZONE = 0.075;

/* ONE THROW, READ AND DRAWN.
 *
 * The knob was clamped at 0.72 of the half-width and the input saturated at
 * 0.88, so the knob stopped moving at 0.36 of the pad while the magnitude kept
 * climbing from 0.818 to 1.000 through 0.44 — the difference between walking
 * and running had no readout at all. Measured at 390x844 the knob froze at
 * 54.3px and at 320x568 at 42.7px, in both cases with the top 18% of the speed
 * range invisible under the thumb.
 *
 * `controls.css` states the intent as "where the knob is IS where you are
 * going, exactly". Two constants cannot express that; one can.
 */
const THROW = 0.88;
/** ...and past this, the pad lets go of that finger. The pointer is captured,
 *  so a finger that is no longer over the pad keeps steering it — fine while
 *  it is near, and wrong once the pad has MOVED out from under it, which is
 *  what rotating the phone with UP held does. */
export const LEASH = 0.85;

/** Where the stick is pushed, as a unit vector and a deflection 0..1, or null
 *  inside the deadzone. y is screen-down, as everywhere else here. */
export function padVector(x, y, width) {
  // A PAD WITH NO SIZE COMMANDS NOTHING. `#controls` is `display:none` on a
  // desktop layout, so `getBoundingClientRect()` is all zeros — and with
  // `width = 0` the deadzone is zero wide, so `hypot(0,0) < 0` is false and a
  // pointer at the exact centre came back as a live direction.
  if (!(width > 0)) return null;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const m = Math.hypot(x, y);
  if (m < width * DEADZONE) return null;
  // full deflection a little before the rim, so the last few millimetres of
  // travel are not dead and a thumb does not have to reach the edge — but
  // not so early that the top fifth of the throw is wasted too
  const mag = Math.min(1, m / (width * 0.5 * THROW));
  return { x: x / m, y: y / m, mag };
}

/** Is this pointer commanding anything? The ownership rules below are written
 *  in terms of it: a finger resting in the deadzone is a hand on the phone. */
export function padLive(x, y, width) {
  return padVector(x, y, width) !== null;
}

export class Pad {
  /* FIELDS, DECLARED.
   *
   * The migration's single biggest source of type errors — 4,646 of about
   * 5,300 — was this: properties assigned in methods rather than in the
   * constructor are invisible to the checker, so every read of one was an
   * error. Declaring them is what turns a class from a bag into a shape, and
   * a shape is what catches the defect that started this: `drawWorld`'s parts
   * object was missing `tiles` and nothing said so until it was reported from
   * play.
   *
   * They start as `any` because 44,000 lines cannot be typed in one step and
   * a wrong type is worse than a missing one. Narrowing them is the work that
   * follows, and it is worth doing in the order the bugs came from.
   */
  owner: any;
  pointers: any;
  steering: any;
  width: any;


  constructor() {
    /** every finger on the glass, in the order they landed, with the last
     *  place each was seen — a resting palm sends no move event, so handing it
     *  the pad without a position left the player walking in the direction the
     *  finger that just LIFTED had been holding */
    this.pointers = new Map();
    this.owner = null;
    this.steering = true;
    this.width = 1;
  }

  /** @returns {boolean} did anything change */
  down(id, x, y, width) {
    this.width = width;
    this.pointers.set(id, { x, y });
    // First finger wins, and EVERY finger is a candidate — recording only the
    // winner meant a second thumb rolling on before the first lifted became a
    // ghost that had to lift and press again. A pointer in the deadzone may
    // not own the pad at all: the heel of a hand landing near centre used to
    // take it and refuse every subsequent thumb, which is the ordinary
    // one-handed grip.
    if (this.owner !== null && this.owner !== id) return false;
    if (!padLive(x, y, width)) return false;
    this.owner = id;
    return true;
  }

  move(id, x, y, width) {
    this.width = width;
    // A MOVE FROM A FINGER WE HAVE NO RECORD OF IS STILL A FINGER. `release()`
    // empties the map — it is called on blur, on `visibilitychange`, on
    // `pagehide` and on a rotation — and `move` used to ignore any pointer not
    // already in it. So rotating the phone with a thumb down killed that thumb
    // *permanently*: sliding it back over the stick did nothing, because the
    // only way back into the map was a fresh `down`, and the finger had
    // already pressed. Measured: `input.up` false for 20 of 20 frames after a
    // rotation, and still false after moving the finger onto the stick.
    // Adopting it costs nothing — a pointermove is proof of a finger.
    this.pointers.set(id, { x, y });
    // a finger that started in the deadzone takes the pad the moment it leaves
    if (this.owner === null && padLive(x, y, width)) {
      this.owner = id;
      return true;
    }
    if (this.owner !== id) return false;
    // THE LEASH DROPS ONE FINGER, NOT ALL OF THEM. This called `release()`,
    // which clears the whole map — so dragging past the rim and back without
    // lifting left the stick dead until a re-press, and, worse, a second
    // finger resting on the stick that never went anywhere died with it.
    // Dropping the owner and handing over is what `up()` already does.
    if (Math.hypot(x, y) > width * LEASH) { this.drop(id); return true; }
    return true;
  }

  /** One finger left. If it owned the pad, hand it to another that is still
   *  down rather than stopping the player dead. */
  up(id) {
    this.pointers.delete(id);
    this.handOff(id);
  }

  /** The owner is no longer steering, but is still on the glass — it slipped
   *  the leash. It keeps its place in the map so that sliding back onto the
   *  stick picks it up again without a lift. */
  drop(id) {
    this.handOff(id);
  }

  handOff(id) {
    if (this.owner !== id) return;
    this.owner = null;
    for (const [other, at] of this.pointers) {
      if (other !== id && padLive(at.x, at.y, this.width)) { this.owner = other; break; }
    }
  }

  release() { this.owner = null; this.pointers.clear(); }

  /** WHERE THE STICK POINTS. The only question this class answers.
   *
   *  THE WHOLE POINT: nothing is stored, so nothing has to be restored. A
   *  launch, a fade or the map sets `steering` false and this returns null;
   *  when it goes true again the same finger produces the same answer with no
   *  recovery step and nothing for a well-meaning `held.clear()` to destroy.
   *
   *  `x` and `y` are a unit direction; `mag` is how far the stick is pushed,
   *  so a light touch is a walk and a full push is a run. */
  vector() {
    if (!this.steering || this.owner === null) return null;
    const at = this.pointers.get(this.owner);
    if (!at) return null;
    return padVector(at.x, at.y, this.width);
  }

  /** Where to draw the knob: the thumb's offset from centre, clamped to the
   *  base, or null when nothing is holding it. */
  knob() {
    if (this.owner === null) return null;
    const at = this.pointers.get(this.owner);
    if (!at) return null;
    const m = Math.hypot(at.x, at.y);
    const lim = this.width * 0.5 * THROW;
    if (m <= lim || m === 0) return { x: at.x, y: at.y, live: this.steering };
    return { x: (at.x / m) * lim, y: (at.y / m) * lim, live: this.steering };
  }
}
